import { useEffect, useMemo, useRef, useState } from "react";
import { getUrl, uploadData } from "aws-amplify/storage";
import { post, generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";
import awsconfig from "../aws-exports";
import {
  createCodingAnalysis,
  deleteCodingAnalysis,
  listCodingAnalyses,
  updateCodingAnalysis,
} from "../graphql/codingAnalysis";
import "./CodingReviewScreen.css";
import SendToEBridgeButton from "./SendToEBridgeButton";

const client = generateClient();

const SUPPORTED_EXTENSIONS = ["pdf", "txt", "docx", "rtf", "md"];
const ACTIVE_ANALYSIS_STATUSES = new Set(["UPLOADING", "QUEUED", "PROCESSING"]);
const DOCUMENT_RETENTION_DAYS = 7;
const DOCUMENT_URL_EXPIRATION_SECONDS = 300;
const SESSION_SOURCED_ANALYSIS_PREFIX = "coding-sess-";
const PLACE_OF_SERVICE_OPTIONS = [
  "Office",
  "Assisted Living",
  "SNF",
  "Home",
  "Outpatient",
  "In patient",
  "ER",
];
const CODING_LEGAL_NOTICE_PARAGRAPHS = [
  "CPT copyright 2025 American Medical Association. All rights reserved. Fee schedules, relative value units, conversion factors and/or related components are not assigned by the AMA, are not part of CPT, and the AMA is not recommending their use. The AMA does not directly or indirectly practice medicine or dispense medical services. The AMA assumes no liability for data contained or not contained herein. CPT is a registered trademark of the American Medical Association.",
  "The responsibility for the content of any \"National Correct Coding Policy\" included in this product is with the Centers for Medicare and Medicaid Services and no endorsement by the AMA is intended or should be implied. The AMA disclaims responsibility for any consequences or liability attributable to or related to any use, nonuse or interpretation of information contained in this product.",
];

function normalizeProcessingStatus(status) {
  return String(status || "UNKNOWN").toUpperCase();
}

function inferContentType(file) {
  if (file.type) return file.type;
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "txt") return "text/plain";
  if (ext === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (ext === "rtf") return "application/rtf";
  if (ext === "md") return "text/markdown";
  return "application/octet-stream";
}

function extensionAllowed(fileName) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return SUPPORTED_EXTENSIONS.includes(ext);
}

function parseResultJson(resultJson) {
  if (!resultJson) return null;
  try {
    const parsed = JSON.parse(resultJson);
    return parsed && typeof parsed === "object" ? normalizeDisplayResult(parsed) : null;
  } catch {
    return null;
  }
}

function buildCodingApiError(payload, httpStatus, fallbackMessage) {
  const message =
    payload?.error || payload?.message || fallbackMessage || "Coding analysis request failed.";
  const err = new Error(message);
  err.code = payload?.code || `HTTP_${Number(httpStatus || 0) || 500}`;
  err.retryable = Boolean(payload?.retryable);
  err.httpStatus = Number(httpStatus || 0) || 500;
  err.processingStatus = String(payload?.processingStatus || "FAILED").toUpperCase();
  return err;
}

function normalizeCodingApiError(apiErr) {
  const message = apiErr?.message || "Coding analysis request failed.";
  const code = apiErr?.code || "REQUEST_FAILED";
  const retryable = Boolean(apiErr?.retryable);
  const processingStatus = String(
    apiErr?.processingStatus || (retryable ? "PROCESSING" : "FAILED")
  ).toUpperCase();

  return {
    message,
    code,
    retryable,
    processingStatus,
  };
}

function parseErrorJson(errorJson) {
  if (!errorJson) return null;
  try {
    const parsed = JSON.parse(errorJson);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function isEmCode(code) {
  const normalized = String(code || "").trim();
  if (!/^\d{5}$/.test(normalized)) return false;
  const numericCode = Number(normalized);
  return numericCode >= 99202 && numericCode <= 99499;
}

function normalizeDisplayResult(result) {
  const parsed = result && typeof result === "object" ? { ...result } : null;
  if (!parsed) return null;

  const cptCodes = asArray(parsed.cptCodes);
  const emIndex = cptCodes.findIndex((item) => isEmCode(item?.code));
  const currentEmCode = String(parsed?.emSuggestion?.suggestedCode || "")
    .trim()
    .toUpperCase();

  if (emIndex >= 0 && (!currentEmCode || currentEmCode === "NA")) {
    const emEntry = cptCodes[emIndex] || {};
    parsed.emSuggestion = {
      suggestedCode: String(emEntry.code || "").trim(),
      rationale: String(emEntry.rationale || parsed?.emSuggestion?.rationale || "").trim(),
      confidence: ["low", "medium", "high"].includes(parsed?.emSuggestion?.confidence)
        ? parsed.emSuggestion.confidence
        : "medium",
    };
  }

  parsed.cptCodes = cptCodes.filter((item, index) => !isEmCode(item?.code) || index !== emIndex);
  return parsed;
}

function getRetentionDeadline(createdAt) {
  const createdTime = new Date(createdAt).getTime();
  if (!Number.isFinite(createdTime)) return null;
  return new Date(createdTime + DOCUMENT_RETENTION_DAYS * 24 * 60 * 60 * 1000);
}

function isRetentionExpired(createdAt) {
  const deadline = getRetentionDeadline(createdAt);
  return deadline ? Date.now() >= deadline.getTime() : false;
}

function getDocumentDisplayName(key) {
  const normalized = String(key || "").trim();
  if (!normalized) return "Document";
  const parts = normalized.split("/").filter(Boolean);
  return parts[parts.length - 1] || normalized;
}

function buildStorageObjectLocator(key) {
  const normalized = String(key || "").replace(/^\/+/, "");
  if (!normalized) {
    return { key: "", options: { accessLevel: "private" } };
  }

  if (
    normalized.startsWith("private/") ||
    normalized.startsWith("protected/") ||
    normalized.startsWith("public/")
  ) {
    return { path: normalized };
  }

  return {
    key: normalized,
    options: {
      accessLevel: "private",
    },
  };
}

function sanitizeUploadFileName(name) {
  const cleaned = String(name || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.slice(0, 180) || `document-${Date.now()}`;
}

function sanitizeSessionIdForAnalysisId(sessionId) {  return String(sessionId || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "")
    .slice(0, 64);
}

function createAnalysisId(sourceSessionId) {
  const timestamp = Date.now();
  const safeSourceSessionId = sanitizeSessionIdForAnalysisId(sourceSessionId);
  if (!safeSourceSessionId) return `coding-${timestamp}`;
  return `${SESSION_SOURCED_ANALYSIS_PREFIX}${safeSourceSessionId}-${timestamp}`;
}

function extractSourceSessionId(analysisId) {
  const match = String(analysisId || "").match(/^coding-sess-([a-zA-Z0-9_-]+)-\d+$/);
  return match?.[1] || "";
}

function escapeHtml(input) {
  return String(input || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildShareText(selected, parsedResult, rawJson) {
  const lines = [];
  lines.push("Medical Coding Review Report");
  lines.push(`Case: ${selected?.caseName || "Untitled"}`);
  lines.push(`Status: ${selected?.processingStatus || "UNKNOWN"}`);
  lines.push(`Created: ${selected?.createdAt ? new Date(selected.createdAt).toLocaleString() : "n/a"}`);

  if (selected?.patientReference) {
    lines.push(`Patient Reference: ${selected.patientReference}`);
  }

  const meta = parsedResult?.analysisMetadata;
  if (meta && typeof meta === "object") {
    lines.push(
      `Extraction Quality Score: ${
        Number.isFinite(Number(meta.textQualityScore)) ? Number(meta.textQualityScore) : "n/a"
      }/100`
    );
    lines.push(`OCR Used: ${meta.ocrUsed ? "Yes" : "No"}`);
    lines.push(
      `Low-Signal Documents: ${Number.isFinite(Number(meta.lowSignalDocumentCount)) ? Number(meta.lowSignalDocumentCount) : 0}`
    );
    lines.push(
      `Avg Noisy Line Ratio: ${Number.isFinite(Number(meta.noisyLineRatioAverage)) ? Number((Number(meta.noisyLineRatioAverage) * 100).toFixed(1)) : "n/a"}%`
    );
  }

  if (!parsedResult) {
    lines.push("");
    lines.push("Raw Result:");
    lines.push(rawJson || "No result data available.");
    return lines.join("\n");
  }

  lines.push("");
  lines.push("Summary");
  lines.push(parsedResult.summary || "No summary available.");

  const em = parsedResult.emSuggestion || {};
  lines.push("");
  lines.push("E&M Suggestion");
  lines.push(`Code: ${em.suggestedCode || "Not provided"}`);
  lines.push(`Confidence: ${em.confidence || "low"}`);
  lines.push(`Rationale: ${em.rationale || "Not provided"}`);

  const sections = [
    ["CPT Codes", asArray(parsedResult.cptCodes), (i) => `${i.code || ""} | ${i.description || ""} | ${i.rationale || ""}`],
    ["ICD-10 Codes", asArray(parsedResult.icd10Codes), (i) => `${i.code || ""} | ${i.description || ""} | ${i.rationale || ""}`],
    ["Modifiers", asArray(parsedResult.modifiers), (i) => `${i.modifier || ""} | ${i.appliesTo || ""} | ${i.rationale || ""}`],
    ["Units", asArray(parsedResult.units), (i) => `${i.code || ""} | ${i.units || ""} | ${i.rationale || ""}`],
  ];

  sections.forEach(([title, items, mapper]) => {
    lines.push("");
    lines.push(title);
    if (!items.length) {
      lines.push("None identified.");
      return;
    }
    items.forEach((item, idx) => {
      lines.push(`${idx + 1}. ${mapper(item)}`);
    });
  });

  lines.push("");
  lines.push("Compliance Flags");
  const complianceFlags = asArray(parsedResult.complianceFlags);
  lines.push(complianceFlags.length ? complianceFlags.join("\n") : "None identified.");

  lines.push("");
  lines.push("Missing Documentation");
  const missingDocs = asArray(parsedResult.missingDocumentation);
  lines.push(missingDocs.length ? missingDocs.join("\n") : "None identified.");

  return lines.join("\n");
}

function buildPrintableHtml(selected, parsedResult, rawJson) {
  const shareText = buildShareText(selected, parsedResult, rawJson)
    .split("\n")
    .map((line) => escapeHtml(line))
    .join("<br />");
  const legalNoticeHtml = CODING_LEGAL_NOTICE_PARAGRAPHS.map(
    (paragraph) => `<p>${escapeHtml(paragraph)}</p>`
  ).join("");

  return `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Medical Coding Review Report</title>
    <style>
      body { font-family: "Segoe UI", Tahoma, sans-serif; color: #0f172a; margin: 1.25rem; }
      h1 { margin: 0 0 0.75rem; font-size: 1.25rem; }
      .report { border: 1px solid #dbe2ea; border-radius: 10px; padding: 1rem; }
      .legal-notice { margin-top: 1rem; border: 1px solid #fcd34d; border-radius: 10px; background: #fffbeb; padding: 0.9rem; }
      .legal-notice h2 { margin: 0 0 0.45rem; font-size: 0.98rem; }
      .legal-notice p { margin: 0; font-size: 0.85rem; line-height: 1.45; color: #374151; }
      .legal-notice p + p { margin-top: 0.65rem; }
      @media print { body { margin: 0.5in; } }
    </style>
  </head>
  <body>
    <h1>Medical Coding Review Report</h1>
    <div class="report">${shareText}</div>
    <div class="legal-notice">
      <h2>CPT and NCCI Notice</h2>
      ${legalNoticeHtml}
    </div>
  </body>
</html>`;
}

function ReportTable({ title, items, columns }) {
  const safeItems = asArray(items);

  return (
    <div className="coding-report-section">
      <h4>{title}</h4>
      {!safeItems.length ? (
        <p className="text-muted">None identified.</p>
      ) : (
        <div className="coding-report-table-wrap">
          <table className="coding-report-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safeItems.map((item, idx) => (
                <tr key={`${title}-${idx}`}>
                  {columns.map((col) => (
                    <td key={col.key}>{item?.[col.key] || "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CodingLegalNotice() {
  return (
    <div className="coding-report-section coding-legal-notice">
      <h4>CPT and NCCI Notice</h4>
      {CODING_LEGAL_NOTICE_PARAGRAPHS.map((paragraph, idx) => (
        <p key={`notice-${idx}`}>{paragraph}</p>
      ))}
    </div>
  );
}

export default function CodingReviewScreen() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const autoSubmitSignatureRef = useRef("");
  const [caseName, setCaseName] = useState("");
  const [patientReference, setPatientReference] = useState("");
  const [placeOfService, setPlaceOfService] = useState("");
  const [noteTemplate, setNoteTemplate] = useState("STANDARD_MEDICAL");
  const [importedSourceSessionId, setImportedSourceSessionId] = useState("");
  const [autoSubmitConfirmOpen, setAutoSubmitConfirmOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [historySearch, setHistorySearch] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingAnalysisId, setDeletingAnalysisId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [resultActionMessage, setResultActionMessage] = useState("");

  const codingApiName = process.env.REACT_APP_CODING_API_NAME || "accountRequestsAPI";
  const codingSubmitPath =
    process.env.REACT_APP_CODING_SUBMIT_PATH || "/coding-analysis/submit";
  const codingDeletePath =
    process.env.REACT_APP_CODING_DELETE_PATH || "/coding-analysis/delete";

  const fileListText = useMemo(() => {
    if (!files.length) return "No files selected";
    return `${files.length} file(s): ${files.map((f) => f.name).join(", ")}`;
  }, [files]);

  const loadHistory = async () => {
    if (!user?.username) return;
    setLoadingHistory(true);
    try {
      const res = await client.graphql({
        query: listCodingAnalyses,
        variables: {
          filter: { userId: { eq: user.username } },
          limit: 100,
        },
      });
      const items = res?.data?.listCodingAnalyses?.items || [];
      const sorted = [...items].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setHistory(sorted);
      setSelected((prev) => {
        if (!prev?.id) return prev;
        const updated = sorted.find((item) => item.id === prev.id);
        return updated || prev;
      });
    } catch (e) {
      console.error("Failed to load coding analyses", e);
      setError(
        "Could not load coding analysis history yet. Run amplify push/codegen if model is newly added."
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  const hasActiveAnalyses = useMemo(
    () => history.some((item) => ACTIVE_ANALYSIS_STATUSES.has(normalizeProcessingStatus(item.processingStatus))),
    [history]
  );

  useEffect(() => {
    if (!user?.username || !hasActiveAnalyses) return undefined;

    const interval = setInterval(() => {
      loadHistory();
    }, 8000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, hasActiveAnalyses]);

  const submitToCodingApi = async ({
    analysisId,
    userId,
    identityId,
    caseName,
    patientReference,
    placeOfService,
    noteTemplate,
    inputDocumentKeys,
    forceRetry = false,
  }) => {
    const response = await post({
      apiName: codingApiName,
      path: codingSubmitPath,
      options: {
        body: {
          analysisId,
          userId,
          identityId,
          caseName,
          patientReference,
          placeOfService,
          noteTemplate,
          inputDocumentKeys,
          forceRetry,
          bucketName: awsconfig.aws_user_files_s3_bucket,
        },
        headers: {
          "Content-Type": "application/json",
        },
      },
    }).response;

    const httpStatus = Number(response?.statusCode || response?.status || 200);
    const payload = await response.body.json();

    if (httpStatus >= 400 || payload?.ok === false) {
      throw buildCodingApiError(payload, httpStatus, `Coding analysis request failed (${httpStatus}).`);
    }

    const processingStatus = normalizeProcessingStatus(
      payload?.processingStatus || (httpStatus === 202 ? "PROCESSING" : "COMPLETED")
    );
    const hasResult = payload?.result && typeof payload.result === "object";

    return {
      processingStatus,
      hasResult,
      result: payload?.result,
      code: payload?.code || null,
      retryable: Boolean(payload?.retryable),
      message:
        processingStatus === "PROCESSING"
          ? payload?.message ||
            "Analysis accepted and still processing. Refresh in a few seconds for results."
          : "Documents submitted successfully.",
    };
  };

  const resetForm = () => {
    setCaseName("");
    setPatientReference("");
    setPlaceOfService("");
    setNoteTemplate("STANDARD_MEDICAL");
    setImportedSourceSessionId("");
    setFiles([]);
    setStatus("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    setError("");

    const invalid = picked.filter((f) => !extensionAllowed(f.name));
    if (invalid.length) {
      setError(
        `Unsupported file type(s): ${invalid
          .map((f) => f.name)
          .join(", ")}. Allowed: ${SUPPORTED_EXTENSIONS.join(", ")}`
      );
      return;
    }

    setFiles(picked);
  };

  const submitForAnalysisWithInputs = async ({
    caseNameValue,
    patientReferenceValue,
    placeOfServiceValue,
    noteTemplateValue,
    filesValue,
    sourceSessionIdValue,
  }) => {
    setError("");
    setStatus("");

    if (!user?.username) {
      setError("You must be signed in.");
      return;
    }

    if (!filesValue.length) {
      setError("Please upload at least one document.");
      return;
    }

    if (!caseNameValue.trim()) {
      setError("Please provide a case name.");
      return;
    }

    setSubmitting(true);

    const analysisId = createAnalysisId(sourceSessionIdValue);
    const keyPrefix = `${user.username}/coding-docs/${analysisId}`;
    const authSession = await fetchAuthSession();
    const identityId = authSession?.identityId || null;

    try {
      setStatus("Creating analysis record...");
      await client.graphql({
        query: createCodingAnalysis,
        variables: {
          input: {
            id: analysisId,
            userId: user.username,
            caseName: caseNameValue.trim(),
            patientReference: patientReferenceValue.trim() || null,
            inputDocumentKeys: [],
            processingStatus: "UPLOADING",
          },
        },
      });

      setStatus("Uploading documents...");
      const uploadedKeys = [];

      for (const file of filesValue) {
        const key = `${keyPrefix}/${sanitizeUploadFileName(file.name)}`;
        const uploadResult = await uploadData({
          key,
          data: file,
          options: {
            accessLevel: "private",
            contentType: inferContentType(file),
          },
        }).result;

        // Use the canonical key from Amplify Storage output whenever available.
        const persistedKey = uploadResult?.key || key;
        uploadedKeys.push(persistedKey);
      }

      setStatus("Queueing coding analysis...");

      await client.graphql({
        query: updateCodingAnalysis,
        variables: {
          input: {
            id: analysisId,
            inputDocumentKeys: uploadedKeys,
            processingStatus: "PROCESSING",
          },
        },
      });

      // Dedicated API call for coding analysis (separate from PCC integration).
      // If the API is not deployed yet, we keep the record as QUEUED for later processing.
      try {
        const apiResult = await submitToCodingApi({
          analysisId,
          userId: user.username,
          identityId,
          caseName: caseNameValue.trim(),
          patientReference: patientReferenceValue.trim() || null,
          placeOfService: placeOfServiceValue || null,
          noteTemplate: noteTemplateValue || "STANDARD_MEDICAL",
          inputDocumentKeys: uploadedKeys,
        });

        const updateInput = {
          id: analysisId,
          processingStatus: apiResult.processingStatus,
          errorJson:
            apiResult.processingStatus === "PROCESSING"
              ? JSON.stringify({
                  message: apiResult.message,
                  code: apiResult.code || "PROCESSING",
                  retryable: true,
                })
              : null,
        };

        if (apiResult.hasResult) {
          updateInput.resultJson = JSON.stringify(apiResult.result);
        }

        await client.graphql({
          query: updateCodingAnalysis,
          variables: {
            input: updateInput,
          },
        });

        setStatus(apiResult.message);
      } catch (apiErr) {
        console.warn("Coding API not ready or unreachable", apiErr);

        const apiDetails = normalizeCodingApiError(apiErr);

        await client.graphql({
          query: updateCodingAnalysis,
          variables: {
            input: {
              id: analysisId,
              processingStatus: apiDetails.processingStatus,
              errorJson: JSON.stringify({
                message: apiDetails.message,
                code: apiDetails.code,
                retryable: apiDetails.retryable,
              }),
            },
          },
        });

        if (apiDetails.retryable || apiDetails.processingStatus === "PROCESSING") {
          setStatus(
            `${apiDetails.message} The request can be retried or may complete after a short delay.`
          );
        } else {
          setError(apiDetails.message);
        }
      }

      resetForm();
      await loadHistory();
    } catch (e) {
      console.error("Failed to submit coding analysis", e);
      setError(e?.message || "Failed to submit coding analysis.");

      try {
        await client.graphql({
          query: updateCodingAnalysis,
          variables: {
            input: {
              id: analysisId,
              processingStatus: "FAILED",
              errorJson: JSON.stringify({
                message: e?.message || "Submission failure",
              }),
            },
          },
        });
      } catch (updateErr) {
        console.warn("Could not persist failure status", updateErr);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitForAnalysis = async () => {
    await submitForAnalysisWithInputs({
      caseNameValue: caseName,
      patientReferenceValue: patientReference,
      placeOfServiceValue: placeOfService,
      noteTemplateValue: noteTemplate,
      filesValue: files,
      sourceSessionIdValue: importedSourceSessionId,
    });
  };

  const clearAutoSubmitRouteState = () => {
    navigate(location.pathname, { replace: true, state: null });
  };

  const confirmAutoSubmit = async () => {
    setAutoSubmitConfirmOpen(false);
    clearAutoSubmitRouteState();
    await submitForAnalysisWithInputs({
      caseNameValue: caseName,
      patientReferenceValue: patientReference,
      placeOfServiceValue: placeOfService,
      noteTemplateValue: noteTemplate,
      filesValue: files,
      sourceSessionIdValue: importedSourceSessionId,
    });
  };

  const cancelAutoSubmit = () => {
    setAutoSubmitConfirmOpen(false);
    clearAutoSubmitRouteState();
    setStatus("Session note imported. Review or edit values, then submit when ready.");
  };

  useEffect(() => {
    const routeState = location.state;
    if (!routeState?.autoSubmit || routeState?.source !== "session-note") return;

    const generatedDocs = Array.isArray(routeState.generatedDocuments)
      ? routeState.generatedDocuments
      : [];

    const signature = JSON.stringify({
      caseName: routeState?.prefill?.caseName || "",
      patientReference: routeState?.prefill?.patientReference || "",
      placeOfService: routeState?.prefill?.placeOfService || "",
      noteTemplate: routeState?.prefill?.noteTemplate || "",
      docs: generatedDocs.map((doc) => ({
        name: String(doc?.name || ""),
        type: String(doc?.type || "text/plain"),
        size: String(doc?.content || "").length,
      })),
    });

    if (!signature || autoSubmitSignatureRef.current === signature) return;
    autoSubmitSignatureRef.current = signature;

    const caseNameDefault = String(routeState?.prefill?.caseName || "").trim();
    const patientReferenceDefault = String(routeState?.prefill?.patientReference || "").trim();
    const placeOfServiceDefault = String(routeState?.prefill?.placeOfService || "").trim();
    const noteTemplateDefault =
      String(routeState?.prefill?.noteTemplate || "").trim().toUpperCase() === "BEHAVIOR_THERAPY"
        ? "BEHAVIOR_THERAPY"
        : "STANDARD_MEDICAL";
    const sourceSessionId = String(routeState?.sourceSessionId || "").trim();

    const generatedFiles = generatedDocs
      .map((doc, index) => {
        const name = String(doc?.name || "generated-note.txt").trim() || `generated-note-${index + 1}.txt`;
        const type = String(doc?.type || "text/plain");
        const content = String(doc?.content || "");
        return new File([content], name, { type });
      })
      .filter((file) => extensionAllowed(file.name));

    if (!caseNameDefault) {
      setError("Could not auto-start coding review because Case Name was missing.");
      return;
    }

    if (!generatedFiles.length) {
      setError("Could not auto-start coding review because no supported note document was generated.");
      return;
    }

    setCaseName(caseNameDefault);
    setPatientReference(patientReferenceDefault);
    setPlaceOfService(placeOfServiceDefault);
    setNoteTemplate(noteTemplateDefault);
    setImportedSourceSessionId(sourceSessionId);
    setFiles(generatedFiles);
    setStatus("Session note imported. Confirm and optionally adjust details before analysis starts.");
    setAutoSubmitConfirmOpen(true);
  }, [location.state, user?.username]);

  const selectedSourceSessionId = useMemo(
    () => extractSourceSessionId(selected?.id),
    [selected?.id]
  );

  const retrySelectedAnalysis = async () => {
    if (!selected?.id || !user?.username) return;

    const selectedError = parseErrorJson(selected.errorJson);
    if (!selectedError?.retryable) {
      setError("This analysis is not retryable.");
      return;
    }

    const keys = Array.isArray(selected.inputDocumentKeys) ? selected.inputDocumentKeys : [];
    if (!keys.length) {
      setError("Cannot retry because no input document keys are stored for this analysis.");
      return;
    }

    setSubmitting(true);
    setError("");
    setStatus("Retrying coding analysis...");

    try {
      const selectedParsed = parseResultJson(selected?.resultJson);
      const retryPlaceOfService = String(
        selectedParsed?.analysisMetadata?.placeOfService || ""
      ).trim();
      const retryNoteTemplate =
        String(selectedParsed?.analysisMetadata?.noteTemplate || "").trim().toUpperCase() ===
        "BEHAVIOR_THERAPY"
          ? "BEHAVIOR_THERAPY"
          : "STANDARD_MEDICAL";

      const authSession = await fetchAuthSession();
      const identityId = authSession?.identityId || null;

      await client.graphql({
        query: updateCodingAnalysis,
        variables: {
          input: {
            id: selected.id,
            processingStatus: "PROCESSING",
            errorJson: null,
          },
        },
      });

      const apiResult = await submitToCodingApi({
        analysisId: selected.id,
        userId: user.username,
        identityId,
        caseName: selected.caseName || "Untitled Case",
        patientReference: selected.patientReference || null,
        placeOfService: retryPlaceOfService || null,
        noteTemplate: retryNoteTemplate,
        inputDocumentKeys: keys,
        forceRetry: true,
      });

      const updateInput = {
        id: selected.id,
        processingStatus: apiResult.processingStatus,
        errorJson:
          apiResult.processingStatus === "PROCESSING"
            ? JSON.stringify({
                message: apiResult.message,
                code: apiResult.code || "PROCESSING",
                retryable: true,
              })
            : null,
      };

      if (apiResult.hasResult) {
        updateInput.resultJson = JSON.stringify(apiResult.result);
      }

      await client.graphql({
        query: updateCodingAnalysis,
        variables: {
          input: updateInput,
        },
      });

      setStatus(apiResult.message);
      await loadHistory();
    } catch (apiErr) {
      const apiDetails = normalizeCodingApiError(apiErr);

      await client.graphql({
        query: updateCodingAnalysis,
        variables: {
          input: {
            id: selected.id,
            processingStatus: apiDetails.processingStatus,
            errorJson: JSON.stringify({
              message: apiDetails.message,
              code: apiDetails.code,
              retryable: apiDetails.retryable,
            }),
          },
        },
      });

      if (apiDetails.retryable || apiDetails.processingStatus === "PROCESSING") {
        setStatus(
          `${apiDetails.message} The request can be retried or may complete after a short delay.`
        );
      } else {
        setError(apiDetails.message);
      }

      await loadHistory();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedResultPretty = useMemo(() => {
    if (!selected?.resultJson) return "No analysis result yet.";
    try {
      return JSON.stringify(JSON.parse(selected.resultJson), null, 2);
    } catch {
      return selected.resultJson;
    }
  }, [selected]);

  const selectedResultParsed = useMemo(
    () => parseResultJson(selected?.resultJson),
    [selected?.resultJson]
  );
  const selectedErrorParsed = useMemo(
    () => parseErrorJson(selected?.errorJson),
    [selected?.errorJson]
  );

  const selectedEmSuggestion = selectedResultParsed?.emSuggestion || null;
  const selectedEmCode = String(selectedEmSuggestion?.suggestedCode || "").trim().toUpperCase();
  const selectedEmIsNotApplicable = selectedEmCode === "NA";
  const selectedAnalysisMeta = selectedResultParsed?.analysisMetadata || null;
  const selectedLowSignalDocumentCount = Number(selectedAnalysisMeta?.lowSignalDocumentCount || 0);
  const selectedNoisyLineRatioAverage = Number(selectedAnalysisMeta?.noisyLineRatioAverage || 0);
  const selectedLowCodingYield = Boolean(selectedAnalysisMeta?.lowCodingYield);
  const selectedQualityScore = Number(selectedAnalysisMeta?.textQualityScore || 0);
  const selectedNoisyLineRatioPercent = Number.isFinite(selectedNoisyLineRatioAverage)
    ? Number((selectedNoisyLineRatioAverage * 100).toFixed(1))
    : null;
  const showExtractionWarning =
    selectedLowSignalDocumentCount > 0 ||
    (Number.isFinite(selectedNoisyLineRatioAverage) && selectedNoisyLineRatioAverage >= 0.3);
  const showScoreYieldMismatchWarning = selectedLowCodingYield && selectedQualityScore >= 70;
  const selectedDocumentKeys = asArray(selected?.inputDocumentKeys);
  const selectedDocumentRetentionDeadline = getRetentionDeadline(selected?.createdAt);
  const selectedDocumentAccessExpired = isRetentionExpired(selected?.createdAt);

  const filteredHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    if (!term) return history;

    return history.filter((item) => {
      const caseNameValue = String(item?.caseName || "").toLowerCase();
      const patientRefValue = String(item?.patientReference || "").toLowerCase();
      return caseNameValue.includes(term) || patientRefValue.includes(term);
    });
  }, [history, historySearch]);

  const copyShareableReport = async () => {
    if (!selected) return;
    const reportText = buildShareText(selected, selectedResultParsed, selectedResultPretty);

    try {
      await navigator.clipboard.writeText(reportText);
      setResultActionMessage("Report copied to clipboard.");
    } catch (copyErr) {
      console.warn("Clipboard write failed", copyErr);
      setResultActionMessage("Could not copy report. Browser clipboard permission may be blocked.");
    }
  };

  const printReport = () => {
    if (!selected) return;

    try {
      const html = buildPrintableHtml(selected, selectedResultParsed, selectedResultPretty);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");

      const cleanup = () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      iframe.onload = () => {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) {
          cleanup();
          setResultActionMessage("Unable to start print preview.");
          return;
        }

        frameWindow.focus();
        setTimeout(() => {
          frameWindow.print();
        }, 100);

        frameWindow.onafterprint = cleanup;
        setTimeout(cleanup, 30000);
      };

      iframe.srcdoc = html;
      document.body.appendChild(iframe);
      setResultActionMessage("Opening print dialog...");
    } catch (printErr) {
      console.warn("Print failed", printErr);
      setResultActionMessage("Unable to print report in this browser session.");
    }
  };

  const openSourceDocument = async (documentKey) => {
    if (!selected?.id) return;
    if (selectedDocumentAccessExpired) {
      setResultActionMessage("Uploaded documents expire after 7 days and are no longer available.");
      return;
    }

    try {
      const locator = buildStorageObjectLocator(documentKey);
      const urlResult = await getUrl({
        ...locator,
        options: {
          ...(locator.options || {}),
          expiresIn: DOCUMENT_URL_EXPIRATION_SECONDS,
          validateObjectExistence: true,
          contentDisposition: {
            type: "inline",
            filename: getDocumentDisplayName(documentKey),
          },
        },
      });

      const signedUrl = urlResult?.url ? String(urlResult.url) : "";
      if (!signedUrl) {
        throw new Error("Storage URL was not generated.");
      }

      window.open(signedUrl, "_blank", "noopener,noreferrer");
      setResultActionMessage(`Opened ${getDocumentDisplayName(documentKey)}.`);
    } catch (docErr) {
      console.warn("Failed to open uploaded source document", docErr);
      setResultActionMessage(
        `Could not open ${getDocumentDisplayName(documentKey)}. It may have expired or been removed.`
      );
    }
  };

  const deleteAnalysisEntry = async (item) => {
    if (!item?.id) return;

    const confirmed = window.confirm(
      `Delete analysis \"${item.caseName || "Untitled"}\" and remove its uploaded document copies?`
    );
    if (!confirmed) return;

    setDeletingAnalysisId(item.id);
    setError("");
    setStatus("");
    setResultActionMessage("");

    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";

      const response = await post({
        apiName: codingApiName,
        path: codingDeletePath,
        options: {
          body: {
            analysisId: item.id,
            ...(accessToken ? { accessToken } : {}),
          },
          headers: {
            "Content-Type": "application/json",
          },
        },
      }).response;

      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || `Delete failed (${httpStatus}).`);
      }

      await client.graphql({
        query: deleteCodingAnalysis,
        variables: {
          input: {
            id: item.id,
          },
        },
      });

      if (selected?.id === item.id) {
        setSelected(null);
      }

      setStatus("Analysis deleted.");
      await loadHistory();
    } catch (deleteErr) {
      console.error("Failed to delete coding analysis", deleteErr);
      setError(deleteErr?.message || "Failed to delete analysis.");
    } finally {
      setDeletingAnalysisId("");
    }
  };

  return (
    <div className="coding-page">
      <div className="coding-header">
        <div>
          <h2>Medical Coding Review</h2>
          <p className="coding-subtitle">
            Upload one or more clinical documents for AI coding review (CPT, ICD-10,
            modifiers, units, and E&M rationale).
          </p>
        </div>
        <button className="button" onClick={loadHistory} disabled={loadingHistory}>
          {loadingHistory ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="coding-grid">
        <div className="coding-column">
          <section className="coding-card">
            <h3>New Analysis</h3>

            {autoSubmitConfirmOpen && (
              <div className="coding-import-modal-backdrop" role="dialog" aria-modal="true">
                <div className="coding-import-modal">
                  <h4>Start Coding Analysis?</h4>
                  <div className="coding-import-preview">
                    <div>
                      <strong>Case Name:</strong> {caseName || "(Required)"}
                    </div>
                    <div>
                      <strong>Patient Reference:</strong> {patientReference || "(Optional)"}
                    </div>
                    <div>
                      <strong>Place of Service:</strong> {placeOfService || "(Optional)"}
                    </div>
                    <div>
                      <strong>Note Template:</strong>{" "}
                      {noteTemplate === "BEHAVIOR_THERAPY" ? "Behavior Therapy Note" : "Standard Medical Note"}
                    </div>
                    <div>
                      <strong>Documents:</strong> {files.length}
                    </div>
                    {importedSourceSessionId && (
                      <div>
                        <strong>Source Session:</strong> {importedSourceSessionId}
                      </div>
                    )}
                  </div>
                  <div className="coding-actions">
                    <button
                      className="button save"
                      type="button"
                      onClick={confirmAutoSubmit}
                      disabled={!caseName.trim() || !files.length || submitting}
                    >
                      {submitting ? "Submitting..." : "Start Coding Analysis"}
                    </button>
                    <button className="button" type="button" onClick={cancelAutoSubmit}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="coding-field">
              <label className="coding-label">Case Name</label>
              <input
                value={caseName}
                onChange={(e) => setCaseName(e.target.value)}
                placeholder="Example: Follow-up coding review 07-01"
              />
            </div>

            <div className="coding-field">
              <label className="coding-label">Patient Reference (optional)</label>
              <input
                value={patientReference}
                onChange={(e) => setPatientReference(e.target.value)}
                placeholder="MRN or internal reference"
              />
            </div>

            <div className="coding-field">
              <label className="coding-label">Place of Service (optional)</label>
              <select value={placeOfService} onChange={(e) => setPlaceOfService(e.target.value)}>
                <option value="">Select place of service...</option>
                {PLACE_OF_SERVICE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {importedSourceSessionId && (
              <div className="coding-field">
                <label className="coding-label">Source Session</label>
                <div className="coding-source-session-pill">{importedSourceSessionId}</div>
              </div>
            )}

            <div className="coding-field">
              <label className="coding-label">Documents</label>
              <input ref={fileInputRef} type="file" multiple onChange={onFilePick} />
              <div className="coding-status">{fileListText}</div>
              <div className="coding-status">Accepted: {SUPPORTED_EXTENSIONS.join(", ")}</div>
            </div>

            <div className="coding-actions">
              <button className="button save" onClick={submitForAnalysis} disabled={submitting}>
                {submitting ? "Submitting..." : "Submit for Coding Analysis"}
              </button>
              <button className="button danger" onClick={resetForm} disabled={submitting}>
                Clear
              </button>
            </div>

            {status && <div className="coding-status">{status}</div>}
            {error && <div className="coding-error">{error}</div>}
          </section>

          <section className="coding-card coding-result">
            <h3>Selected Result</h3>
            {selected ? (
              <>
                <p className="coding-list-meta">
                  {selected.caseName || "Untitled"} • {normalizeProcessingStatus(selected.processingStatus)}
                </p>
                {selectedSourceSessionId && (
                  <p className="coding-list-meta">Source Session: {selectedSourceSessionId}</p>
                )}

                <div className="coding-actions">
                  <button className="button" onClick={copyShareableReport}>
                    Copy Shareable Report
                  </button>
                  <button className="button" onClick={printReport}>
                    Print / Save PDF
                  </button>
                  <SendToEBridgeButton
                    documentType="CODING_ANALYSIS"
                    codingAnalysisId={selected.id}
                    enabled={Boolean(selectedResultParsed)}
                  />
                  {selectedErrorParsed?.retryable && (
                    <button className="button save" onClick={retrySelectedAnalysis} disabled={submitting}>
                      {submitting ? "Retrying..." : "Retry Analysis"}
                    </button>
                  )}
                </div>

                {resultActionMessage && <div className="coding-status">{resultActionMessage}</div>}
                {selectedErrorParsed?.message && (
                  <div
                    className={
                      selectedErrorParsed?.retryable ? "coding-status coding-warning" : "coding-error"
                    }
                  >
                    {selectedErrorParsed.message}
                  </div>
                )}

                {selectedResultParsed ? (
                  <div className="coding-report">
                    <div className="coding-report-section">
                      <h4>Summary</h4>
                      <p>{selectedResultParsed.summary || "No summary provided."}</p>
                    </div>

                    {selectedAnalysisMeta && (
                      <div className="coding-report-section">
                        <h4>Extraction Quality</h4>
                        {showExtractionWarning && (
                          <p className="coding-status coding-warning">
                            Extraction quality risk detected. This note appears partially image-based or noisy,
                            which can reduce coding reliability. Uploading a cleaner OCR/text export is recommended.
                          </p>
                        )}
                        {showScoreYieldMismatchWarning && (
                          <p className="coding-status coding-warning">
                            High extraction score but low coding yield detected. The document text volume may be high,
                            but clinically usable coding evidence appears limited.
                          </p>
                        )}
                        <div className="coding-metadata-grid">
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Quality Score</span>
                            <strong>
                              {Number.isFinite(Number(selectedAnalysisMeta.textQualityScore))
                                ? Number(selectedAnalysisMeta.textQualityScore)
                                : "n/a"}
                              /100
                            </strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">OCR Used</span>
                            <strong>{selectedAnalysisMeta.ocrUsed ? "Yes" : "No"}</strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Documents</span>
                            <strong>{selectedAnalysisMeta.documentCount || 0}</strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Extracted Chars</span>
                            <strong>{selectedAnalysisMeta.totalExtractedChars || 0}</strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Low-Signal Docs</span>
                            <strong>{selectedLowSignalDocumentCount}</strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Avg Noisy Lines</span>
                            <strong>
                              {selectedNoisyLineRatioPercent === null ? "n/a" : `${selectedNoisyLineRatioPercent}%`}
                            </strong>
                          </div>
                          <div className="coding-metadata-item">
                            <span className="coding-em-label">Coding Yield</span>
                            <strong>{selectedLowCodingYield ? "Low" : "Normal"}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="coding-em-card">
                      <div>
                        <div className="coding-em-label">Suggested E&amp;M Code</div>
                        <div
                          className={`coding-em-code ${
                            selectedEmIsNotApplicable ? "coding-em-code-na" : ""
                          }`}
                        >
                          {selectedEmIsNotApplicable
                            ? "NA"
                            : selectedEmSuggestion?.suggestedCode || "Not provided"}
                        </div>
                        {selectedEmIsNotApplicable && (
                          <div className="coding-em-na-pill">E&amp;M not applicable</div>
                        )}
                      </div>
                      <div>
                        <div className="coding-em-label">Confidence</div>
                        <div className="coding-em-confidence">
                          {(selectedEmSuggestion?.confidence || "low").toUpperCase()}
                        </div>
                      </div>
                    </div>

                    <div className="coding-report-section">
                      <h4>E&amp;M Rationale</h4>
                      <p>{selectedEmSuggestion?.rationale || "No rationale provided."}</p>
                    </div>

                    {selectedDocumentKeys.length > 0 && (
                      <div className="coding-report-section">
                        <h4>Uploaded Documents</h4>
                        {selectedDocumentRetentionDeadline && (
                          <p className="coding-document-retention-note">
                            {selectedDocumentAccessExpired
                              ? `Document access expired on ${selectedDocumentRetentionDeadline.toLocaleString()}.`
                              : `Available until ${selectedDocumentRetentionDeadline.toLocaleString()}. Documents auto-delete after 7 days.`}
                          </p>
                        )}
                        <ul className="coding-document-list">
                          {selectedDocumentKeys.map((documentKey, idx) => (
                            <li key={`${documentKey}-${idx}`} className="coding-document-list-item">
                              <span>{getDocumentDisplayName(documentKey)}</span>
                              <button
                                className="button"
                                type="button"
                                onClick={() => openSourceDocument(documentKey)}
                                disabled={selectedDocumentAccessExpired}
                              >
                                Open
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <ReportTable
                      title="CPT Codes"
                      items={selectedResultParsed.cptCodes}
                      columns={[
                        { key: "code", label: "Code" },
                        { key: "description", label: "Description" },
                        { key: "rationale", label: "Rationale" },
                      ]}
                    />

                    <ReportTable
                      title="ICD-10 Codes"
                      items={selectedResultParsed.icd10Codes}
                      columns={[
                        { key: "code", label: "Code" },
                        { key: "description", label: "Description" },
                        { key: "rationale", label: "Rationale" },
                      ]}
                    />

                    <ReportTable
                      title="Modifiers"
                      items={selectedResultParsed.modifiers}
                      columns={[
                        { key: "modifier", label: "Modifier" },
                        { key: "appliesTo", label: "Applies To" },
                        { key: "rationale", label: "Rationale" },
                      ]}
                    />

                    <ReportTable
                      title="Units"
                      items={selectedResultParsed.units}
                      columns={[
                        { key: "code", label: "Code" },
                        { key: "units", label: "Units" },
                        { key: "rationale", label: "Rationale" },
                      ]}
                    />

                    <div className="coding-report-section">
                      <h4>Compliance Flags</h4>
                      {asArray(selectedResultParsed.complianceFlags).length ? (
                        <ul className="coding-report-list">
                          {selectedResultParsed.complianceFlags.map((flag, idx) => (
                            <li key={`flag-${idx}`}>{flag}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted">None identified.</p>
                      )}
                    </div>

                    <div className="coding-report-section">
                      <h4>Missing Documentation</h4>
                      {asArray(selectedResultParsed.missingDocumentation).length ? (
                        <ul className="coding-report-list">
                          {selectedResultParsed.missingDocumentation.map((item, idx) => (
                            <li key={`missing-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted">None identified.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <pre className="coding-json">{selectedResultPretty}</pre>
                )}

                <CodingLegalNotice />
              </>
            ) : (
              <p className="text-muted">Select an analysis to view details.</p>
            )}
          </section>
        </div>

        <section className="coding-card">
          <h3>Past Analyses</h3>

          <div className="coding-field">
            <label className="coding-label">Search</label>
            <input
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Search by case name or patient reference"
            />
          </div>

          {!history.length && <p className="text-muted">No analyses found yet.</p>}
          {history.length > 0 && !filteredHistory.length && (
            <p className="text-muted">No analyses match your search.</p>
          )}

          <div className="coding-history-scroll">
            <ul className="coding-list">
              {filteredHistory.map((item) => {
                const isSelected = selected?.id === item.id;
                const itemStatus = normalizeProcessingStatus(item.processingStatus);
                const itemSourceSessionId = extractSourceSessionId(item.id);
                return (
                  <li
                    key={item.id}
                    className={`coding-list-item ${isSelected ? "is-selected" : ""}`}
                    onClick={() => {
                      setSelected(item);
                      setResultActionMessage("");
                    }}
                  >
                    <div className="coding-list-row">
                      <div className="coding-list-title">{item.caseName || "Untitled"}</div>
                      <button
                        className="button danger coding-delete-button"
                        type="button"
                        disabled={deletingAnalysisId === item.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAnalysisEntry(item);
                        }}
                      >
                        {deletingAnalysisId === item.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                    <div className="coding-list-meta">
                      <span
                        className={`coding-status-pill status-${itemStatus.toLowerCase()}`}
                      >
                        {itemStatus}
                      </span>{" "}
                      • {new Date(item.createdAt).toLocaleString()}
                    </div>
                    <div className="coding-list-meta">
                      {item.patientReference ? `Ref: ${item.patientReference}` : "No reference"}
                    </div>
                    {itemSourceSessionId && (
                      <div className="coding-list-meta">Source Session: {itemSourceSessionId}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
